'use client';

import type { FooterProps } from '@routeroyal/theme-core';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';
import { ArrowRight, Facebook, Globe, Instagram, Linkedin, Loader2, Twitter } from 'lucide-react';
import Link from 'next/link';
import { useCallback, useState } from 'react';
import { AdaptiveLogo } from './AdaptiveLogo';

export interface StoneInvestmentFooterProps extends FooterProps {
  logoUrl?: string;
}

type NewsletterStatus = 'idle' | 'submitting' | 'success' | 'error';

/**
 * Stone Investment Footer Component
 * 
 * Concept One Design (Exact Match):
 * - Newsletter section at top with "The Art of Living" tagline
 * - 4-column editorial grid (Editorial, Collection, Discover, Corporate)
 * - Social icons with hover effects
 * - Legal compliance bar at bottom
 * - font-serif for headings, font-sans for body
 */
export function StoneInvestmentFooter({
  companyName: _companyName = 'Stone Investment',
  tagline: _tagline,
  contactEmail: _contactEmail,
  contactPhone: _contactPhone,
  address,
  socialLinks,
  legalLinks: _legalLinks = [],
  quickLinks: _quickLinks = [],
  messages,
  locale = 'en',
  logoUrl,
}: StoneInvestmentFooterProps) {
  const companyName = 'Stone Investment';
  const [email, setEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<NewsletterStatus>('idle');
  
  const currentYear = new Date().getFullYear();
  
  // Get stone-specific translations
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const footerMessages = (stoneMessages.footer || {}) as Record<string, unknown>;
  const sectionsMessages = (footerMessages.sections || {}) as Record<string, string>;
  const editorialMessages = (footerMessages.editorial || {}) as Record<string, string>;
  const collectionMessages = (footerMessages.collection || {}) as Record<string, string>;
  const discoverMessages = (footerMessages.discover || {}) as Record<string, string>;
  const corporateMessages = (footerMessages.corporate || {}) as Record<string, string>;
  const legalMessages = (footerMessages.legal || {}) as Record<string, string>;
  
  const accessMessages = ((stoneMessages.accessibility || {}) as Record<string, string>);

  const t = (key: string, fallback: string): string => {
    return (footerMessages[key] as string) || fallback;
  };

  const handleNewsletterSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setNewsletterStatus('error');
      setTimeout(() => setNewsletterStatus('idle'), 4000);
      return;
    }

    setNewsletterStatus('submitting');
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      if (!res.ok) throw new Error('Subscription failed');

      setNewsletterStatus('success');
      setEmail('');
      analyticsEvents.custom('cta_click', {
        cta_text: 'newsletter_subscribe',
        cta_location: 'footer_newsletter',
        cta_destination: '/api/newsletter/subscribe',
        cta_variant: 'newsletter',
      });
      analyticsEvents.generateLead('newsletter', undefined, { email: trimmed });
    } catch {
      setNewsletterStatus('error');
    }
    setTimeout(() => setNewsletterStatus('idle'), 4000);
  }, [email]);

  return (
    <footer 
      className="bg-stone-900 text-stone-300 py-24 border-t border-stone-800 font-sans relative z-10"
      role="contentinfo"
      aria-label={accessMessages.siteFooter || 'Site footer'}
    >
      <div className="container mx-auto px-6 md:px-12">
        
        {/* Top Section: Newsletter & Brand Promise */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-24 pb-12 border-b border-stone-800">
          <div className="max-w-lg mb-12 md:mb-0">
            <h3 className="font-serif text-3xl md:text-4xl text-stone-100 mb-6 italic">
              {t('artOfLiving', 'The Art of Living')}
            </h3>
            <p className="font-light text-sm leading-relaxed text-stone-300">
              {t('newsletterText', 'Join our private circle to receive curated off-market properties, architectural insights, and invitations to exclusive events.')}
            </p>
          </div>

          <div className="w-full md:w-auto">
            {newsletterStatus === 'success' ? (
              <p className="text-sm uppercase tracking-[0.15em] text-stone-100 font-sans py-3">
                {t('newsletterSuccess', 'You are on the list')}
              </p>
            ) : (
              <form
                className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 group w-full"
                onSubmit={handleNewsletterSubmit}
              >
                <input 
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('emailPlaceholder', 'Email Address')} 
                  className="bg-transparent border-b border-stone-700 py-3 w-full sm:w-80 text-stone-100 placeholder-stone-400 focus:outline-none focus:border-stone-300 transition-colors"
                  aria-label={t('emailPlaceholder', 'Email Address')}
                  required
                  disabled={newsletterStatus === 'submitting'}
                />
                <button
                  type="submit"
                  className="text-stone-300 group-hover:text-stone-100 transition-colors disabled:opacity-50"
                  aria-label={
                    newsletterStatus === 'submitting'
                      ? t('newsletterSubmitting', 'Joining…')
                      : t('subscribe', 'Subscribe')
                  }
                  disabled={newsletterStatus === 'submitting'}
                >
                  {newsletterStatus === 'submitting' ? (
                    <Loader2 size={20} strokeWidth={1} className="animate-spin" />
                  ) : (
                    <ArrowRight size={20} strokeWidth={1} />
                  )}
                </button>
              </form>
            )}
            {newsletterStatus === 'error' && (
              <p className="mt-3 text-xs uppercase tracking-widest text-stone-400 font-sans" role="alert">
                {t('newsletterError', 'Please try again shortly')}
              </p>
            )}
          </div>
        </div>

        {/* Middle Section: Navigation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-24">
          
          {/* Brand Identity */}
          <div className="md:col-span-4 space-y-8">
            <div className="flex flex-col items-start">
              <AdaptiveLogo
                logoUrl={logoUrl}
                fallbackText="Stone Investment"
                colorMode="light"
                height={48}
                className="text-left items-start"
              />
            </div>
            
            <div className="text-sm font-light leading-relaxed space-y-4">
              <div>
                <p className="text-stone-100 font-medium mb-1">{t('headquarters', 'Headquarters')}</p>
                <p className="text-stone-200">{address?.street || '64, rue Sainte'}</p>
                <p className="text-stone-200">{address?.city || '13001 Marseille'}, {address?.country || 'France'}</p>
              </div>
              <div>
                <p className="text-stone-100 font-medium mb-1">{t('indianOcean', 'Indian Ocean')}</p>
                <p className="text-stone-200">{t('mauritiusAddress1', 'Black River Gorges')}</p>
                <p className="text-stone-200">{t('mauritiusAddress2', '90403 Grande Rivière, Mauritius')}</p>
          </div>
        </div>

            <div className="flex gap-6 pt-2">
              {socialLinks?.instagram ? (
                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="text-stone-300 hover:text-stone-100 transition-colors" aria-label="Instagram">
                  <Instagram size={18} strokeWidth={1} aria-hidden="true" />
                </a>
              ) : (
                <button className="text-stone-300 hover:text-stone-100 transition-colors" aria-label="Instagram"><Instagram size={18} strokeWidth={1} aria-hidden="true" /></button>
              )}
              {socialLinks?.linkedin ? (
                <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="text-stone-300 hover:text-stone-100 transition-colors" aria-label="LinkedIn">
                  <Linkedin size={18} strokeWidth={1} aria-hidden="true" />
                </a>
              ) : (
                <button className="text-stone-300 hover:text-stone-100 transition-colors" aria-label="LinkedIn"><Linkedin size={18} strokeWidth={1} aria-hidden="true" /></button>
              )}
              {socialLinks?.facebook ? (
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-stone-300 hover:text-stone-100 transition-colors" aria-label="Facebook">
                  <Facebook size={18} strokeWidth={1} aria-hidden="true" />
                </a>
              ) : (
                <button className="text-stone-300 hover:text-stone-100 transition-colors" aria-label="Facebook"><Facebook size={18} strokeWidth={1} aria-hidden="true" /></button>
              )}
              <button className="text-stone-300 hover:text-stone-100 transition-colors" aria-label="Twitter"><Twitter size={18} strokeWidth={1} aria-hidden="true" /></button>
            </div>
          </div>

          {/* Column 1: Editorial */}
          <div className="md:col-span-2 space-y-6">
            <p className="text-stone-50 text-xs uppercase tracking-widest font-medium mb-4" role="heading" aria-level={2}>{sectionsMessages.editorial || 'Editorial'}</p>
            <ul className="space-y-3 text-sm font-light tracking-wide text-stone-300">
              <li><Link href={`/${locale}/news`} className="hover:text-stone-100 transition-colors">{editorialMessages.theJournal || 'The Journal'}</Link></li>
            </ul>
          </div>

          {/* Column 2: Collection */}
          <div className="md:col-span-2 space-y-6">
            <p className="text-stone-50 text-xs uppercase tracking-widest font-medium mb-4" role="heading" aria-level={2}>{sectionsMessages.collection || 'Collection'}</p>
            <ul className="space-y-3 text-sm font-light tracking-wide text-stone-300">
              <li><Link href={`/${locale}/properties?region=mauritius`} className="hover:text-stone-100 transition-colors">{collectionMessages.mauritius || 'Mauritius'}</Link></li>
              <li><Link href={`/${locale}/properties?region=provence`} className="hover:text-stone-100 transition-colors">{collectionMessages.provence || 'Provence'}</Link></li>
            </ul>
          </div>

          {/* Column 3: Discover */}
          <div className="md:col-span-2 space-y-6">
            <p className="text-stone-50 text-xs uppercase tracking-widest font-medium mb-4" role="heading" aria-level={2}>{sectionsMessages.discover || 'Discover'}</p>
            <ul className="space-y-3 text-sm font-light tracking-wide text-stone-300">
              <li><Link href={`/${locale}/areas`} className="hover:text-stone-100 transition-colors">{discoverMessages.areaGuides || 'Area Guides'}</Link></li>
              <li><Link href={`/${locale}/guides`} className="hover:text-stone-100 transition-colors">{discoverMessages.buyerGuides || 'Buyer Guides'}</Link></li>
              <li><Link href={`/${locale}/news?category=market-insights`} className="hover:text-stone-100 transition-colors">{discoverMessages.marketInsights || 'Market Insights'}</Link></li>
            </ul>
          </div>

          {/* Column 4: Corporate */}
          <div className="md:col-span-2 space-y-6">
            <p className="text-stone-50 text-xs uppercase tracking-widest font-medium mb-4" role="heading" aria-level={2}>{sectionsMessages.corporate || 'Corporate'}</p>
            <ul className="space-y-3 text-sm font-light tracking-wide text-stone-300">
              <li><Link href={`/${locale}/about`} className="hover:text-stone-100 transition-colors">{corporateMessages.aboutStone || 'About Stone Investment'}</Link></li>
              <li><Link href={`/${locale}/contact`} className="hover:text-stone-100 transition-colors">{corporateMessages.contact || 'Contact'}</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom Section: Compliance & Legal */}
        <div className="border-t border-stone-800 pt-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 text-sm uppercase tracking-widest text-stone-200">
          <div className="flex flex-col md:flex-row gap-4 md:gap-8">
            <p>© {currentYear} {companyName}</p>
            <div className="hidden md:block w-px h-3 bg-stone-700" aria-hidden="true" />
            <div className="flex flex-wrap gap-6">
              <Link href={`/${locale}/privacy`} className="hover:text-stone-100 transition-colors">{legalMessages.privacyPolicy || 'Privacy Policy'}</Link>
              <Link href={`/${locale}/terms`} className="hover:text-stone-100 transition-colors">{legalMessages.termsOfUse || 'Terms of Use'}</Link>
              <Link href={`/${locale}/gdpr`} className="hover:text-stone-100 transition-colors">{legalMessages.gdpr || 'GDPR'}</Link>
              <Link href={`/${locale}/cookies`} className="hover:text-stone-100 transition-colors">{legalMessages.cookies || 'Cookies'}</Link>
              <Link href={`/${locale}/legal`} className="hover:text-stone-100 transition-colors">{legalMessages.legalNotice || 'Legal Notice'}</Link>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Globe size={12} aria-hidden="true" />
            <span>{t('global', 'Global')} - {locale.toUpperCase()}</span>
          </div>
        </div>

        {/* Structured Data for SEO */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'RealEstateAgent',
              name: companyName,
              contactPoint: {
                '@type': 'ContactPoint',
                contactType: 'customer service',
                ...(_contactEmail && { email: _contactEmail }),
                ...(_contactPhone && { telephone: _contactPhone }),
              },
              address: {
                '@type': 'PostalAddress',
                streetAddress: address?.street || '64, rue Sainte',
                addressLocality: address?.city || '13001 Marseille',
                ...(address?.postalCode && { postalCode: address.postalCode }),
                addressCountry: address?.country || 'France',
              },
              sameAs: Object.values(socialLinks || {}).filter(Boolean),
            }),
          }}
        />

      </div>
    </footer>
  );
}
